import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  FilterListOff as FilterOffIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import { AdsTableProps, Campaign, Ad, AdSet, SortField } from '../../../types/ads.types';
import { CampaignRow } from './CampaignRow';
import { AdRow } from './AdRow';
import { FilterRow } from './FilterRow';
import { applyFilters } from '../../../utils/filterUtils';

/**
 * AdsTable - Main table component for Ads Manager
 *
 * Features:
 * - Hierarchical view (Campaign > AdSet > Ads)
 * - Tabular view (flat list of ads)
 * - Sorting by multiple fields
 * - Filtering by status, metrics, text search
 * - Row selection
 * - Expandable campaigns
 * - Trend charts (daily/hourly)
 *
 * @example
 * <AdsTable
 *   ads={unifiedAds}
 *   viewMode="levels"
 *   breakdownMode="daily"
 *   showGraphs={true}
 *   filters={filters}
 *   filterScope="ads"
 *   searchMode="contains"
 *   sortField="spend"
 *   sortDirection="desc"
 *   selectedAds={selectedAds}
 *   onSort={handleSort}
 *   onSelectAd={handleSelectAd}
 *   onSelectAll={handleSelectAll}
 *   onUpdateStatus={handleUpdateStatus}
 *   onUpdateBudget={handleUpdateBudget}
 * />
 */
export const AdsTable: React.FC<AdsTableProps> = ({
  ads,
  viewMode,
  breakdownMode,
  showGraphs,
  filters,
  filterScope,
  searchMode,
  sortField,
  sortDirection,
  selectedAds,
  loading,
  updatingAdsetId,
  updatingAdId,
  expandedCampaigns: controlledExpandedCampaigns,
  expandedAds,
  expandedDates,
  searchQuery,
  onSort,
  onSelectAd,
  onSelectAll,
  onUpdateStatus,
  onUpdateBudget,
  onDuplicate,
  onExpandedCampaignsChange,
  onExpandedAdsChange,
  onExpandedDatesChange,
  onFiltersChange,
  onSearchChange,
  onSearchModeChange,
  onFilterScopeChange,
}) => {
  const [internalExpandedCampaigns, setInternalExpandedCampaigns] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState<boolean>(true);

  // Use controlled or internal state
  const expandedCampaigns = controlledExpandedCampaigns ?? internalExpandedCampaigns;
  const setExpandedCampaigns = onExpandedCampaignsChange ?? setInternalExpandedCampaigns;

  // Group ads into campaigns
  const campaigns = useMemo(() => {
    return groupAdsByCampaign(ads);
  }, [ads]);

  // Apply filters
  const filteredCampaigns = useMemo(() => {
    return applyFilters(campaigns, filters, filterScope, searchMode);
  }, [campaigns, filters, filterScope, searchMode]);

  // Apply sorting
  const sortedCampaigns = useMemo(() => {
    return sortCampaigns(filteredCampaigns, sortField, sortDirection);
  }, [filteredCampaigns, sortField, sortDirection]);

  // Get flat list of filtered ads (for tabular view and selection)
  const filteredAds = useMemo(() => {
    return sortedCampaigns.flatMap((c) => c.ads);
  }, [sortedCampaigns]);


  // Handle campaign expand/collapse
  const toggleCampaign = (campaignId: string) => {
    const next = new Set(expandedCampaigns);
    if (next.has(campaignId)) {
      next.delete(campaignId);
    } else {
      next.add(campaignId);
    }
    setExpandedCampaigns(next);
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    onSelectAll(checked);
  };

  // Check if all visible ads are selected
  const allSelected = filteredAds.length > 0 && filteredAds.every((ad) => selectedAds.has(ad.adId));
  const someSelected = filteredAds.some((ad) => selectedAds.has(ad.adId)) && !allSelected;

  return (
    <>
      {/* Filter Toggle Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          px: 2.5,
          py: 1.25,
          bgcolor: showFilters ? '#f0f9ff' : 'background.paper',
          borderBottom: 1,
          borderColor: showFilters ? 'primary.light' : 'divider',
          transition: 'all 0.2s ease',
        }}
      >
        {/* Left: Filter Toggle + Filter Scope */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              bgcolor: showFilters ? 'primary.main' : 'grey.100',
              borderRadius: 1.5,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: showFilters ? 'primary.dark' : 'grey.200',
              },
            }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <IconButton
              size="small"
              sx={{
                color: showFilters ? 'white' : 'text.secondary',
                p: 0,
                '&:hover': {
                  bgcolor: 'transparent',
                },
              }}
            >
              {showFilters ? <FilterOffIcon fontSize="small" /> : <FilterIcon fontSize="small" />}
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                color: showFilters ? 'white' : 'text.primary',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Typography>
          </Box>

          {/* Filter Scope - always visible in levels view */}
          {viewMode === 'levels' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500, whiteSpace: 'nowrap', fontSize: '0.813rem' }}
              >
                Filters apply to:
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  bgcolor: '#f3f4f6',
                  borderRadius: 1,
                  p: 0.5,
                  gap: 0.5,
                }}
              >
                <Box
                  onClick={() => onFilterScopeChange('campaign')}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 0.75,
                    fontSize: '0.813rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    bgcolor: filterScope === 'campaign' ? 'primary.main' : 'white',
                    color: filterScope === 'campaign' ? 'white' : 'text.secondary',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: filterScope === 'campaign' ? 'primary.dark' : '#f9fafb',
                    },
                  }}
                >
                  Campaigns
                </Box>
                <Box
                  onClick={() => onFilterScopeChange('ads')}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 0.75,
                    fontSize: '0.813rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    bgcolor: filterScope === 'ads' ? 'primary.main' : 'white',
                    color: filterScope === 'ads' ? 'white' : 'text.secondary',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: filterScope === 'ads' ? 'primary.dark' : '#f9fafb',
                    },
                  }}
                >
                  Ads
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Center/Right: Search Section */}
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, flex: 1 }}>
          {/* Search Mode Selector - Fixed width on left */}
          <FormControl size="small">
            <Select
              value={searchMode}
              onChange={(e) => onSearchModeChange(e.target.value as 'contains' | 'notContains')}
              sx={{
                bgcolor: 'white',
                height: '100%',
                minWidth: 140,
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: 1.125,
                },
              }}
            >
              <MenuItem value="contains" sx={{ fontSize: '0.875rem' }}>Contains</MenuItem>
              <MenuItem value="notContains" sx={{ fontSize: '0.875rem' }}>Not Contains</MenuItem>
            </Select>
          </FormControl>

          {/* Search Input - Fills remaining space */}
          <TextField
            placeholder="Search campaigns, ads, adsets, IDs..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiInputBase-root': {
                bgcolor: 'white',
                fontSize: '0.875rem',
                height: '100%',
              },
              '& .MuiInputBase-input': {
                py: 1.125,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '85vh',
          overflow: 'auto',
          '& .MuiTableCell-root': {
            fontSize: '0.813rem',
          },
        }}
      >
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              '& > th': {
                bgcolor: '#f9fafb',
              },
            }}
          >
            {/* Checkbox */}
            <TableCell padding="checkbox" sx={{ width: 35, bgcolor: '#f9fafb' }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
                size="small"
              />
            </TableCell>

            {/* Status */}
            <TableCell sx={{ width: 70, bgcolor: '#f9fafb', fontWeight: 600 }}>STATUS</TableCell>

            {/* Budget */}
            <TableCell sx={{ width: 100, bgcolor: '#f9fafb', fontWeight: 600 }}>BUDGET</TableCell>

            {/* Campaign/Ad Name */}
            <TableCell sx={{ width: 350, bgcolor: '#f9fafb', fontWeight: 600 }}>
              {viewMode === 'levels' ? 'CAMPAIGN' : 'AD'}
            </TableCell>

            {/* Advertiser */}
            <TableCell sx={{ width: 110, bgcolor: '#f9fafb', fontWeight: 600 }}>
              ADVERTISER
            </TableCell>

            {/* Sortable Metric Columns */}
            <SortableHeaderCell
              label="SPEND"
              field="spend"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableHeaderCell
              label="REVENUE"
              field="revenue"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableHeaderCell
              label="P&L"
              field="pnl"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableHeaderCell
              label="ROAS"
              field="roas"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
            />

            {/* Sortable metric columns */}
            <SortableHeaderCell
              label="RPM"
              field="rpm"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={65}
            />
            <SortableHeaderCell
              label="RPC"
              field="rpc"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={65}
            />
            <SortableHeaderCell
              label="CPA"
              field="cpa"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={65}
            />
            <SortableHeaderCell
              label="CPC"
              field="cpc"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={65}
            />
            <SortableHeaderCell
              label="CLICKS"
              field="clicks"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={60}
            />
            <SortableHeaderCell
              label="SEARCHES"
              field="searches"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={60}
            />
            <SortableHeaderCell
              label="PAGE CTR"
              field="pageCtr"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={70}
            />
            <SortableHeaderCell
              label="CREATED"
              field="adCreatedTime"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={90}
              align="center"
            />
            <SortableHeaderCell
              label="UPDATED"
              field="adUpdatedTime"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={90}
              align="center"
            />

            {/* Trend Chart Column */}
            {showGraphs && (
              <TableCell align="center" sx={{ width: 70, bgcolor: '#f9fafb', fontWeight: 600 }}>
                ROAS TREND
              </TableCell>
            )}

            <SortableHeaderCell
              label="COVERAGE"
              field="coverage"
              currentField={sortField}
              direction={sortDirection}
              onSort={onSort}
              width={70}
            />
          </TableRow>


          {/* Filter Row - inside TableHead but NOT sticky */}
          {showFilters && (
            <FilterRow
              filters={filters}
              onFiltersChange={onFiltersChange}
              showGraphs={showGraphs}
              ads={ads}
            />
          )}
        </TableHead>

        <TableBody>
          {loading ? (
            // Loading state
            <TableRow>
              <TableCell colSpan={showGraphs ? 20 : 19} sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading ads...
                </Typography>
              </TableCell>
            </TableRow>
          ) : filteredCampaigns.length === 0 ? (
            // Empty state
            <TableRow>
              <TableCell colSpan={showGraphs ? 20 : 19} sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  No ads found matching your filters
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Try adjusting your search or filter criteria
                </Typography>
              </TableCell>
            </TableRow>
          ) : viewMode === 'levels' ? (
            // Hierarchical view
            sortedCampaigns.map((campaign) => (
              <CampaignRow
                key={campaign.campaignId}
                campaign={campaign}
                expanded={expandedCampaigns.has(campaign.campaignId)}
                selectedAds={selectedAds}
                showGraphs={showGraphs}
                breakdownMode={breakdownMode}
                updatingAdsetId={updatingAdsetId}
                updatingAdId={updatingAdId}
                onToggle={() => toggleCampaign(campaign.campaignId)}
                onSelectAd={onSelectAd}
                onUpdateStatus={onUpdateStatus}
                onUpdateBudget={onUpdateBudget}
                onDuplicate={onDuplicate}
                expandedAds={expandedAds}
                onToggleAdExpansion={(adId) => {
                  if (onExpandedAdsChange && expandedAds) {
                    const newSet = new Set(expandedAds);
                    if (newSet.has(adId)) {
                      newSet.delete(adId);
                    } else {
                      newSet.add(adId);
                    }
                    onExpandedAdsChange(newSet);
                  }
                }}
                expandedDates={expandedDates}
                onToggleDateExpansion={(adId, date) => {
                  if (onExpandedDatesChange && expandedDates) {
                    const newSet = new Set(expandedDates);
                    const key = `${adId}-${date}`;
                    if (newSet.has(key)) {
                      newSet.delete(key);
                    } else {
                      newSet.add(key);
                    }
                    onExpandedDatesChange(newSet);
                  }
                }}
              />
            ))
          ) : (
            // Tabular view
            filteredAds.map((ad) => (
              <AdRow
                key={ad.adId}
                ad={ad}
                selected={selectedAds.has(ad.adId)}
                showGraphs={showGraphs}
                breakdownMode={breakdownMode}
                updatingAdsetId={updatingAdsetId}
                updatingAdId={updatingAdId}
                onSelect={() => onSelectAd(ad.adId)}
                onUpdateStatus={onUpdateStatus}
                onUpdateBudget={onUpdateBudget}
                onDuplicate={onDuplicate}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  );
};

/**
 * Sortable table header cell component
 */
interface SortableHeaderCellProps {
  label: string;
  field: SortField;
  currentField: SortField;
  direction: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  total?: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  label,
  field,
  currentField,
  direction,
  onSort,
  total,
  width = 75,
  align = 'right',
}) => {
  const isActive = currentField === field;

  return (
    <TableCell
      align={align}
      sx={{
        width,
        bgcolor: '#f9fafb',
        fontWeight: 600,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
          bgcolor: '#f3f4f6',
        },
      }}
      onClick={() => onSort(field)}
    >
      <Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: align === 'center' ? 'center' : align === 'left' ? 'flex-start' : 'flex-end',
          gap: 0.5
        }}>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
            {label}
          </Typography>
          {isActive && (
            <Typography variant="caption" component="span">
              {direction === 'asc' ? '↑' : '↓'}
            </Typography>
          )}
        </Box>
        {total && (
          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'primary.main' }}>
            {total}
          </Typography>
        )}
      </Box>
    </TableCell>
  );
};

/**
 * Helper: Group ads by campaign
 */
function groupAdsByCampaign(ads: Ad[]): Campaign[] {
  const campaignMap = new Map<string, Campaign>();

  for (const ad of ads) {
    if (!campaignMap.has(ad.campaignId)) {
      campaignMap.set(ad.campaignId, {
        campaignId: ad.campaignId,
        campaignName: ad.campaignName,
        advertiserName: ad.advertiserName,
        totalSpend: 0,
        totalRevenue: 0,
        totalPnl: 0,
        avgRoas: 0,
        totalClicks: 0,
        totalSearches: 0,
        adsets: [],
        ads: [],
      });
    }

    const campaign = campaignMap.get(ad.campaignId)!;
    campaign.ads.push(ad);

    // Aggregate metrics
    campaign.totalSpend += ad.spend;
    campaign.totalRevenue += ad.revenue;
    campaign.totalPnl += ad.pnl;
    campaign.totalClicks += ad.clicks;
    campaign.totalSearches += ad.searches;
  }

  // Calculate average ROAS
  for (const campaign of campaignMap.values()) {
    campaign.avgRoas = campaign.totalSpend > 0 ? campaign.totalRevenue / campaign.totalSpend : 0;

    // Group ads by adset
    const adsetMap = new Map<string, AdSet>();
    for (const ad of campaign.ads) {
      if (!adsetMap.has(ad.adsetId)) {
        adsetMap.set(ad.adsetId, {
          adsetId: ad.adsetId,
          adsetName: ad.adsetName,
          ads: [],
        });
      }
      adsetMap.get(ad.adsetId)!.ads.push(ad);
    }
    campaign.adsets = Array.from(adsetMap.values());
  }

  return Array.from(campaignMap.values());
}

/**
 * Helper: Sort campaigns by field
 */
function sortCampaigns(campaigns: Campaign[], field: SortField, direction: 'asc' | 'desc'): Campaign[] {
  const sorted = [...campaigns].sort((a, b) => {
    let aVal: number | string;
    let bVal: number | string;

    switch (field) {
      case 'spend':
        aVal = a.totalSpend;
        bVal = b.totalSpend;
        break;
      case 'revenue':
        aVal = a.totalRevenue;
        bVal = b.totalRevenue;
        break;
      case 'pnl':
        aVal = a.totalPnl;
        bVal = b.totalPnl;
        break;
      case 'roas':
        aVal = a.avgRoas;
        bVal = b.avgRoas;
        break;
      case 'clicks':
        aVal = a.totalClicks;
        bVal = b.totalClicks;
        break;
      case 'searches':
        aVal = a.totalSearches;
        bVal = b.totalSearches;
        break;
      case 'rpm':
        // RPM = Revenue per 1000 searches
        aVal = a.totalSearches > 0 ? (a.totalRevenue / a.totalSearches) * 1000 : 0;
        bVal = b.totalSearches > 0 ? (b.totalRevenue / b.totalSearches) * 1000 : 0;
        break;
      case 'rpc':
        // RPC = Revenue per click
        aVal = a.totalClicks > 0 ? a.totalRevenue / a.totalClicks : 0;
        bVal = b.totalClicks > 0 ? b.totalRevenue / b.totalClicks : 0;
        break;
      case 'cpa':
        // CPA = Cost per acquisition (using searches as proxy)
        aVal = a.totalSearches > 0 ? a.totalSpend / a.totalSearches : 0;
        bVal = b.totalSearches > 0 ? b.totalSpend / b.totalSearches : 0;
        break;
      case 'cpc':
        // CPC = Cost per click
        aVal = a.totalClicks > 0 ? a.totalSpend / a.totalClicks : 0;
        bVal = b.totalClicks > 0 ? b.totalSpend / b.totalClicks : 0;
        break;
      case 'pageCtr':
        // Page CTR = Average from all ads
        aVal = a.ads.reduce((sum, ad) => sum + (ad.pageCtr || 0), 0) / a.ads.length;
        bVal = b.ads.reduce((sum, ad) => sum + (ad.pageCtr || 0), 0) / b.ads.length;
        break;
      case 'coverage':
        // Coverage = Average from all ads
        aVal = a.ads.reduce((sum, ad) => sum + (ad.coverage || 0), 0) / a.ads.length;
        bVal = b.ads.reduce((sum, ad) => sum + (ad.coverage || 0), 0) / b.ads.length;
        break;
      case 'adCreatedTime':
        // Sort by earliest created date
        aVal = a.ads.reduce((earliest, ad) => {
          const adTime = ad.adCreatedTime ? new Date(ad.adCreatedTime).getTime() : Infinity;
          return Math.min(earliest, adTime);
        }, Infinity);
        bVal = b.ads.reduce((earliest, ad) => {
          const adTime = ad.adCreatedTime ? new Date(ad.adCreatedTime).getTime() : Infinity;
          return Math.min(earliest, adTime);
        }, Infinity);
        break;
      case 'adUpdatedTime':
        // Sort by latest updated date
        aVal = a.ads.reduce((latest, ad) => {
          const adTime = ad.adUpdatedTime ? new Date(ad.adUpdatedTime).getTime() : 0;
          return Math.max(latest, adTime);
        }, 0);
        bVal = b.ads.reduce((latest, ad) => {
          const adTime = ad.adUpdatedTime ? new Date(ad.adUpdatedTime).getTime() : 0;
          return Math.max(latest, adTime);
        }, 0);
        break;
      default:
        // If field doesn't match, maintain original order
        return 0;
    }

    // Handle numeric comparison
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      if (direction === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    }

    // Handle string comparison (shouldn't happen with current fields)
    return 0;
  });

  return sorted;
}

export default AdsTable;
